import React from 'react';
import { Box, ButtonBase, Typography } from '@mui/material';
import { Company, StatusColor } from './lib/types';
import { Kpis, STATUS_COLORS } from './lib/selectors';
import { CARD_LIFT, FONT, KPI_LABEL, RADIUS, TONE } from './lib/brand';
import { tint } from './lib/tone';

interface Tile {
  key: string;
  label: string;
  value: number;
  sub: string;
  pressed?: boolean;
  dot?: StatusColor;
  hint?: string;
  highlight?: boolean;
  onClick: () => void;
}

/** Six tiles, every one a button: Active, Pending, Idle, Open items, Awaiting Grant, Open Grant asks. */
export function SummaryStrip({
  kpis, company, companyName, statusFilter, onStatus, onOpenItems, onAwaiting, onGa,
}: {
  kpis: Kpis;
  company: Company;
  companyName: string;
  statusFilter: StatusColor | null;
  onStatus: (c: StatusColor | null) => void;
  onOpenItems: () => void;
  onAwaiting: () => void;
  onGa: () => void;
}) {
  const toggle = (c: StatusColor) => () => onStatus(statusFilter === c ? null : c);
  const tiles: Tile[] = [
    { key: 'active', label: `Active · ${companyName}`, value: kpis.active, sub: `/ ${kpis.total}`, dot: 'green', pressed: statusFilter === 'green', onClick: toggle('green') },
    { key: 'pending', label: `Pending decision · ${companyName}`, value: kpis.pending, sub: `/ ${kpis.total}`, dot: 'yellow', pressed: statusFilter === 'yellow', onClick: toggle('yellow') },
    { key: 'idle', label: `Idle · ${companyName}`, value: kpis.idle, sub: `/ ${kpis.total}`, dot: 'red', pressed: statusFilter === 'red', onClick: toggle('red') },
    { key: 'open', label: `Open action items · ${companyName}`, value: kpis.openActions, sub: 'not declined or done', onClick: onOpenItems },
    {
      key: 'awaiting', label: `Awaiting Grant · ${companyName}`, value: kpis.awaiting, highlight: true,
      sub: `${kpis.alternatives} exec alternative${kpis.alternatives === 1 ? '' : 's'}`, onClick: onAwaiting,
    },
    { key: 'ga', label: 'Open Grant asks (GA) · all', value: kpis.gaOpen, sub: `${kpis.secOpen} critical SEC open · MASTER-LEDGER, both cos.`, onClick: onGa },
  ];
  return (
    <Box data-testid="summary-strip" data-company={company} sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, minmax(0,1fr))', md: 'repeat(3, minmax(0,1fr))', lg: 'repeat(6, minmax(0,1fr))' }, gap: 1.5, mb: 2 }}>
      {tiles.map((t) => (
        <ButtonBase
          key={t.key}
          onClick={t.onClick}
          aria-pressed={t.pressed === undefined ? undefined : t.pressed}
          data-testid={`kpi-${t.key}`}
          sx={{
            display: 'flex', flexDirection: 'column', alignItems: 'flex-start', textAlign: 'left', p: 1.6, borderRadius: RADIUS.lg,
            border: '1px solid', borderColor: t.pressed ? (t.dot ? STATUS_COLORS[t.dot] : 'primary.main') : 'divider',
            bgcolor: t.highlight ? tint(TONE.warn.bg) : 'background.paper', ...CARD_LIFT,
          }}
        >
          <Typography variant="caption" sx={{ ...KPI_LABEL, display: 'flex', alignItems: 'center', gap: 0.7, color: 'text.secondary' }}>
            {t.dot && <Box component="span" aria-hidden sx={{ width: 9, height: 9, borderRadius: '50%', bgcolor: STATUS_COLORS[t.dot] }} />}
            {t.label}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.8, mt: 0.4 }}>
            <Typography component="span" sx={{ fontFamily: FONT.display, fontSize: 30, fontWeight: 700, lineHeight: 1.1, color: 'primary.main' }}>{t.value}</Typography>
            <Typography component="span" variant="caption" color="text.secondary">{t.sub}</Typography>
          </Box>
        </ButtonBase>
      ))}
    </Box>
  );
}
